import { useCallback, useEffect, useRef, useState } from 'react'
import { LIFF_ID_BOT1, LIFF_ID_BOT2 } from '../config/env'
import { callOrdercusCf } from '../lib/api/ordercusApi'
import { memberEntryUrl, orderEntryUrl } from '../lib/lineNavigation'
import { markMemberLogin, readMemberSession } from '../member/memberSession'
import { markBoot } from '../lib/perfBoot'
import { markLiffLogin } from '../lib/liffSession'
import {
  getOrderLineUserId,
  inferBotIdFromLineChannel,
  resolveBotIdFromHref,
  resolveOrderIdFromUrl,
  isLineOrderEntryUrl,
} from '../lib/orderUtils'
import type { OrderData } from '../types/order'

export interface LineProfile {
  userId: string
  displayName: string
  pictureUrl: string
}

type LiffSdkModule = typeof import('@line/liff')

let liffModulePromise: Promise<LiffSdkModule> | null = null

export async function loadLiffSdk(): Promise<LiffSdkModule['default']> {
  if (!liffModulePromise) {
    liffModulePromise = import('@line/liff').catch((error) => {
      liffModulePromise = null
      throw error
    })
  }
  const module = await liffModulePromise
  return module.default
}

export function isLineInAppBrowser(): boolean {
  return /Line\//i.test(navigator.userAgent || '')
}

/** เปิดจาก Chrome/Safari ภายนอก (ไม่ใช่ LIFF URL) */
export function isExternalWebBrowser(): boolean {
  const href = window.location.href || ''
  return !isLineInAppBrowser() && !href.includes('liff.line.me')
}

function orderHasLineUser(order: OrderData | null | undefined): boolean {
  return !!getOrderLineUserId(order)
}

function profileFromOrder(order: OrderData): LineProfile {
  return {
    userId: getOrderLineUserId(order),
    displayName: String(order.LineDisplayName || '').trim(),
    pictureUrl: String(order.LinePictureUrl || '').trim(),
  }
}

export function buildLiffEntryUrl(orderId: string, forBotId = '1'): string {
  const bid = forBotId === '2' ? '2' : '1'
  const liffId = bid === '2' ? LIFF_ID_BOT2 : LIFF_ID_BOT1
  const q = new URLSearchParams()
  if (orderId) q.set('orderId', orderId)
  q.set('page', 'customer-order')
  if (bid === '2') q.set('botId', '2')
  const qs = q.toString()
  return `https://liff.line.me/${liffId}${qs ? '?' + qs : ''}`
}

/** Context-aware entry from the member's order list. */
export function buildMemberOrderUrl(orderId: string, forBotId = '1', linkKey = ''): string {
  const bid = forBotId === '2' ? '2' : '1'
  return orderEntryUrl(bid === '2' ? LIFF_ID_BOT2 : LIFF_ID_BOT1, isLineInAppBrowser(), orderId, bid, linkKey)
}

/**
 * หน้า "ออร์เดอร์ของฉัน" ของบอทนั้น — Bot 2 ต้องมี botId=2 (หลัง redirect URL ไม่มี LIFF ID ให้เดา)
 * asOrderId = โหมดแอดมินดูมุมมองลูกค้าของเจ้าของออร์เดอร์นั้น
 */
export function buildMyOrdersUrl(forBotId = '1', asOrderId = ''): string {
  const bid = forBotId === '2' ? '2' : '1'
  const liffId = bid === '2' ? LIFF_ID_BOT2 : LIFF_ID_BOT1
  return memberEntryUrl(liffId, isLineInAppBrowser(), bid, asOrderId)
}

export function useLiff(order: OrderData | null, orderId: string) {
  const [lineProfile, setLineProfile] = useState<LineProfile | null>(null)
  const [botId, setBotId] = useState(() => resolveBotIdFromHref() || '')
  const [liffReady, setLiffReady] = useState(false)
  /** true เฉพาะเมื่อได้โปรไฟล์จาก liff.getProfile() จริง (ไม่ใช่เติมจากข้อมูลออร์เดอร์) */
  const [lineVerified, setLineVerified] = useState(false)
  const lineUserIdRef = useRef('')
  const initPromiseRef = useRef<Promise<void> | null>(null)
  const viewerSyncKeyRef = useRef('')
  const liffSdkRef = useRef<LiffSdkModule['default'] | null>(null)
  // The SDK can remove liff.state after init; retain the entry intent for this page.
  const lineEntryRef = useRef(isLineOrderEntryUrl(orderId) || new URLSearchParams(window.location.search).get('page') === 'customer-order')

  const resolveBotId = useCallback(async (): Promise<string> => {
    const fromHref = resolveBotIdFromHref()
    if (fromHref) return fromHref
    try {
      const stored = sessionStorage.getItem('oc_liff_bot')
      if (stored === '1' || stored === '2') return stored
    } catch {
      /* ignore */
    }
    if (order?.LineChannelID) {
      const inferred = inferBotIdFromLineChannel(order.LineChannelID)
      if (inferred) return inferred
    }
    if (window.__bootCache?.LineChannelID) {
      const inferred = inferBotIdFromLineChannel(window.__bootCache.LineChannelID)
      if (inferred) return inferred
    }
    const oid = orderId || resolveOrderIdFromUrl()
    if (oid && window.__earlyFetchPromise) {
      try {
        const data = await Promise.race([
          window.__earlyFetchPromise,
          new Promise<null>((_, rej) => setTimeout(() => rej(new Error('timeout')), 1200)),
        ])
        if (data?.orderData?.LineChannelID) {
          const inferred = inferBotIdFromLineChannel(data.orderData.LineChannelID)
          if (inferred) return inferred
        }
      } catch {
        /* ignore */
      }
    }
    return ''
  }, [order, orderId])

  const setProfile = useCallback((profile: LineProfile | null, verified = false) => {
    setLineProfile(profile)
    lineUserIdRef.current = profile?.userId || ''
    setLineVerified(verified && !!profile?.userId)
  }, [])

  const hydrateFromOrder = useCallback(
    (od: OrderData) => {
      if (!orderHasLineUser(od)) return false
      const lp = profileFromOrder(od)
      setProfile(lp)
      const inferred = inferBotIdFromLineChannel(od.LineChannelID)
      if (inferred) setBotId(inferred)
      return true
    },
    [setProfile],
  )

  useEffect(() => {
    let cancelled = false

    const finishLiff = () => {
      if (cancelled) return
      markBoot('ordercus:liff-ready')
      setLiffReady(true)
    }

    const init = async () => {
      const external = isExternalWebBrowser()
      const hasCallback = new URLSearchParams(window.location.search).has('code')
      const savedSession = readMemberSession(resolveBotIdFromHref() || inferBotIdFromLineChannel(order?.LineChannelID) || '1', window.location.search)
      if (external && !lineEntryRef.current && !hasCallback && !savedSession) {
        if (order) hydrateFromOrder(order)
        finishLiff()
        return
      }

      const bid = await resolveBotId()
      if (cancelled) return

      const resolvedBot = bid || '1'
      setBotId(resolvedBot)
      try {
        sessionStorage.setItem('oc_liff_bot', resolvedBot)
      } catch {
        /* ignore */
      }

      const liffId = resolvedBot === '2' ? LIFF_ID_BOT2 : LIFF_ID_BOT1
      const liff = await loadLiffSdk()
      liffSdkRef.current = liff
      await liff.init({ liffId })
      if (cancelled) return

      if (resolvedBot === '2') setBotId('2')
      else if (!bid) setBotId('1')

      const hasLine = orderHasLineUser(order)
      const oid = orderId || resolveOrderIdFromUrl()

      if (!liff.isInClient() && isLineInAppBrowser() && !liff.isLoggedIn()) {
        if (hasLine && order) {
          hydrateFromOrder(order)
          finishLiff()
          return
        }
        const redirectKey = `oc_liff_redirect_${oid || 'none'}`
        if (!sessionStorage.getItem(redirectKey)) {
          sessionStorage.setItem(redirectKey, '1')
          try {
            sessionStorage.setItem('oc_liff_bot', resolvedBot)
          } catch {
            /* ignore */
          }
          window.location.replace(buildLiffEntryUrl(oid, resolvedBot))
          return
        }
        console.warn('[LIFF] redirect แล้วแต่ยังไม่ได้ in-client — ใช้ liff.line.me โดยตรง')
      }

      if (liff.isInClient()) {
        try {
          sessionStorage.removeItem(`oc_liff_redirect_${oid || 'none'}`)
        } catch {
          /* ignore */
        }
      }

      if (!liff.isLoggedIn()) {
        // ไม่บังคับ liff.login — เปิดจากเบราว์เซอร์ทั่วไปก็กรอก/ยืนยันออร์เดอร์ได้แบบแขก
        // (ในแอป LINE เปิดผ่าน LIFF ซึ่ง login ให้เองอยู่แล้ว · token แขกบันทึกผู้เปิดล่าสุดไม่ได้ตาม rules)
        if (hasLine && order) hydrateFromOrder(order)
      } else {
        const profile = await liff.getProfile()
        if (cancelled) return
        if (profile?.userId) {
          markLiffLogin(resolvedBot)
          markMemberLogin(resolvedBot)
          setProfile({
            userId: profile.userId,
            displayName: profile.displayName || '',
            pictureUrl: profile.pictureUrl || '',
          }, true)
        }
      }

      finishLiff()
    }

    initPromiseRef.current = init().catch((e) => {
      if (cancelled) return
      console.warn('[LIFF] init:', e)
      if (order) hydrateFromOrder(order)
      markBoot('ordercus:liff-ready')
      setLiffReady(true)
    })

    return () => {
      cancelled = true
    }
  }, [resolveBotId, order, orderId, hydrateFromOrder, setProfile])

  const ensureLineUserId = useCallback(async (): Promise<string> => {
    // ตอนกดยืนยัน ห้ามให้ LIFF init/getProfile ที่ช้า (เน็ตมือถือ) ค้างหน้าบันทึกนานเกินไป
    // ใส่ timeout ต่อขั้น แล้ว fallback ไปข้อมูลที่มีอยู่แล้ว (order.LineUserID) แทน
    const withDeadline = <T,>(p: Promise<T>, ms: number): Promise<T | undefined> =>
      Promise.race([p, new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), ms))])

    if (initPromiseRef.current) {
      try {
        await withDeadline(initPromiseRef.current, 2500)
      } catch {
        /* non-critical */
      }
    }
    if (lineUserIdRef.current) return lineUserIdRef.current

    try {
      const liff =
        liffSdkRef.current || (isLineInAppBrowser() ? await withDeadline(loadLiffSdk(), 2000) : null)
      if (liff) liffSdkRef.current = liff
      if (liff?.isInClient?.() && liff.isLoggedIn?.()) {
        const profile = await withDeadline(liff.getProfile(), 2000)
        if (profile?.userId) {
          setProfile({
            userId: profile.userId,
            displayName: profile.displayName || '',
            pictureUrl: profile.pictureUrl || '',
          }, true)
          return profile.userId
        }
      }
    } catch (e) {
      console.warn('ensureLineUserId:', e)
    }

    if (!lineUserIdRef.current && order) hydrateFromOrder(order)
    return lineUserIdRef.current || getOrderLineUserId(order) || ''
  }, [order, hydrateFromOrder, setProfile])

  const syncLineViewer = useCallback(async () => {
    // บันทึก "ผู้เปิดล่าสุด" + Event Log เฉพาะคนที่ login LINE จริง (ยืนยันแล้ว)
    // เปิดจากเบราว์เซอร์ทั่วไป/ยังไม่ login = ไม่บันทึก · ใช้ uid จากออร์เดอร์แทนไม่ได้ (เป็นของเจ้าของ ไม่ใช่ผู้เปิด)
    const uid = lineVerified ? lineUserIdRef.current : ''
    if (!order || !orderId || !uid) return
    const syncKey = `${orderId}|${uid}`
    if (viewerSyncKeyRef.current === syncKey) return
    viewerSyncKeyRef.current = syncKey
    try {
      // server เป็นคนตัดสิน เจ้าของ/ผู้เปิดอื่น: บัญชีอื่นไม่ทับเจ้าของ (บันทึกเป็นผู้เปิดอื่น)
      const result = await callOrdercusCf('updateLastViewer', {
        orderId,
        lineUserId: uid,
        displayName: lineProfile?.displayName || '',
        pictureUrl: lineProfile?.pictureUrl || '',
        botId: botId || '',
        viewOnly: true,
      })
      if (result.status === 'success') {
        try {
          for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
            const key = sessionStorage.key(index)
            if (key?.startsWith('oc_my_orders_v1:')) sessionStorage.removeItem(key)
          }
        } catch {
          /* private mode */
        }
      }
    } catch (e) {
      console.warn('[LIFF] updateLastViewer:', e)
    }
  }, [orderId, lineProfile, botId, order, lineVerified])

  useEffect(() => {
    if (!liffReady || !orderId) return
    const uid = lineUserIdRef.current || getOrderLineUserId(order)
    if (!uid) return
    const run = () => {
      void syncLineViewer()
    }
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(run, { timeout: 1500 })
      return () => window.cancelIdleCallback?.(idleId)
    }
    const timeoutId = window.setTimeout(run, 500)
    return () => window.clearTimeout(timeoutId)
  }, [liffReady, orderId, lineProfile?.userId, order, syncLineViewer])

  const effectiveLineUserId = lineProfile?.userId || getOrderLineUserId(order)

  /** LIFF ID token ของคนที่ login อยู่ ('' ถ้ายังไม่ init/ไม่ได้ login) — ใช้เรียก memberApi */
  const getIdToken = useCallback((): string => {
    try {
      return liffSdkRef.current?.getIDToken?.() || ''
    } catch {
      return ''
    }
  }, [])

  return {
    lineProfile,
    botId,
    liffReady,
    lineVerified,
    getIdToken,
    effectiveLineUserId,
    ensureLineUserId,
    syncLineViewer,
  }
}
